/**
 * SecondaryStatsGrid.tsx
 *
 * File-level:
 * - Secondary stats grid for admin dashboard showing update-related statistics.
 * - Displays Profile Changes, Update Photos, Update Documents, and Update Videos counts.
 *
 * Notes:
 * - Uses similar gradient styling as primary StatsGrid for visual consistency.
 * - Each card is clickable and emits events to parent components.
 */

import React from 'react';
import { UserCog, Image, FileText, Video } from 'lucide-react';

/**
 * SecondaryStats
 *
 * @description Shape of the secondary stats object consumed by SecondaryStatsGrid.
 */
export interface SecondaryStats {
  /** Count of profile changes/updates */
  profileChanges: number;
  /** Count of photo updates */
  updatePhotos: number;
  /** Count of document updates */
  updateDocuments: number;
  /** Count of video updates */
  updateVideos: number;
}

/**
 * StatCardProps
 *
 * @description Props for the small StatCard component.
 */
interface StatCardProps {
  title: string;
  value: number | string;
  gradient?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  testId?: string;
}

/**
 * StatCard
 *
 * @description Reusable presentational card that displays a stat with a bold numeric value.
 * Uses a full-card gradient background and white text for clear contrast.
 *
 * @param props StatCardProps
 */
function StatCard({ title, value, gradient, icon, onClick, testId }: StatCardProps) {
  const bgStyle = gradient ? { backgroundImage: gradient } : undefined;

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      aria-label={String(title)}
      className="w-full text-left rounded-lg shadow-md transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={bgStyle}
    >
      <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: 'transparent' }}>
        <div
          className="flex items-center justify-center w-12 h-12 rounded-lg bg-white/20"
          aria-hidden="true"
        >
          <div className="w-6 h-6 text-white">{icon}</div>
        </div>

        <div className="flex-1">
          <div className="text-sm font-medium text-white/90">{title}</div>
          <div className="text-2xl font-extrabold text-white mt-1">{value}</div>
        </div>
      </div>
    </button>
  );
}

/**
 * SecondaryStatsGridProps
 *
 * @description Props for SecondaryStatsGrid component.
 */
interface SecondaryStatsGridProps {
  stats: SecondaryStats;
  onCardClick?: (key: keyof SecondaryStats | string) => void;
}

/**
 * getGradientForKey
 *
 * @description Returns a CSS gradient for each secondary stat key.
 *
 * @param key string - stat key
 * @returns string - CSS background-image value
 */
function getGradientForKey(key: string): string {
  switch (key) {
    case 'profileChanges':
      // Purple to blue gradient
      return 'linear-gradient(90deg, rgba(147,51,234,1) 0%, rgba(99,102,241,1) 100%)';
    case 'updatePhotos':
      // Pink to rose gradient
      return 'linear-gradient(90deg, rgba(236,72,153,1) 0%, rgba(244,63,94,1) 100%)';
    case 'updateDocuments':
      // Orange to amber gradient
      return 'linear-gradient(90deg, rgba(249,115,22,1) 0%, rgba(245,158,11,1) 100%)';
    case 'updateVideos':
      // Cyan to blue gradient
      return 'linear-gradient(90deg, rgba(6,182,212,1) 0%, rgba(59,130,246,1) 100%)';
    default:
      return 'linear-gradient(90deg, rgba(99,102,241,1) 0%, rgba(6,182,212,1) 100%)';
  }
}

/**
 * SecondaryStatsGrid
 *
 * @description Renders a responsive grid of secondary stat cards for the admin dashboard.
 * Shows update-related statistics in a 4-column layout on large screens.
 *
 * @param props SecondaryStatsGridProps
 */
export default function SecondaryStatsGrid({ stats, onCardClick }: SecondaryStatsGridProps) {
  const {
    profileChanges,
    updatePhotos,
    updateDocuments,
    updateVideos,
  } = stats;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div className="lg:col-span-1">
        <StatCard
          title="Profile Changes"
          value={profileChanges}
          gradient={getGradientForKey('profileChanges')}
          icon={<UserCog className="w-5 h-5" />}
          onClick={() => onCardClick?.('profileChanges')}
          testId="stat-profile-changes"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Update Photos"
          value={updatePhotos}
          gradient={getGradientForKey('updatePhotos')}
          icon={<Image className="w-5 h-5" />}
          onClick={() => onCardClick?.('updatePhotos')}
          testId="stat-update-photos"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Update Documents"
          value={updateDocuments}
          gradient={getGradientForKey('updateDocuments')}
          icon={<FileText className="w-5 h-5" />}
          onClick={() => onCardClick?.('updateDocuments')}
          testId="stat-update-documents"
        />
      </div>

      <div className="lg:col-span-1">
        <StatCard
          title="Update Videos"
          value={updateVideos}
          gradient={getGradientForKey('updateVideos')}
          icon={<Video className="w-5 h-5" />}
          onClick={() => onCardClick?.('updateVideos')}
          testId="stat-update-videos"
        />
      </div>
    </div>
  );
}
